import { GetParam } from "aldoo-ra/Admin/ParamIO/api"

// Cache for the product types to avoid multiple API calls
let cachedProductTypes = null
let loadingPromise = null
let initialized = false

/**
 * Get store product types from ParamIO
 * @returns {Promise<string[]>} Array of product type strings
 */
export const getStoreProductTypes = async () => {
  // Return cached types if available
  if (cachedProductTypes) {
    return cachedProductTypes
  }

  // If already loading, return the same promise to avoid multiple calls
  if (loadingPromise) {
    return loadingPromise
  }

  // Create the loading promise
  loadingPromise = fetchProductTypes()
  
  try {
    const types = await loadingPromise
    cachedProductTypes = types
    return types
  } catch (error) {
    // Reset loading promise on error so we can retry
    loadingPromise = null
    throw error
  } finally {
    // Clear loading promise
    loadingPromise = null
  }
}

/**
 * Fetch product types from ParamIO API
 * @returns {Promise<string[]>} Array of product type strings
 */
const fetchProductTypes = async () => {
  try {
    const response = await GetParam("STORE_PRODUCT_TYPES")

    // Check if response has the value directly (ParamIO returns the parameter object directly)
    if (response && response.value) {
      const types = response.value
      
      // Validate that it's an array
      if (Array.isArray(types)) {
        return types
      }
    }
  } catch (error) {
    // Silently fall back to defaults on error
  }

  // Fallback to default types if API call fails
  return [
    { name: "purchase", description: "This is a normal product purchase" },
    { name: "subscription", description: "Recurring payment product with auto-renewal" },
    { name: "license", description: "Product that grants access rights or permissions" },
    { name: "bundle", description: "Package containing multiple products together" },
    { name: "credits", description: "Virtual currency or credit pack" }
  ]
}

/**
 * Get store product types for dropdown options
 * @returns {Promise<Array<{value: string, label: string, name: string}>>} Dropdown options
 */
export const getStoreProductTypeOptions = async () => {
  const types = await getStoreProductTypes()
  return types.map(type => {
    if (typeof type === "string") {
      // Legacy format - convert string to object
      return {
        value: type,
        label: type.charAt(0).toUpperCase() + type.slice(1),
        name: type.charAt(0).toUpperCase() + type.slice(1)
      }
    } else if (type && typeof type === "object" && type.name) {
      // New format - object with name and description
      return {
        value: type.name,
        label: type.name.charAt(0).toUpperCase() + type.name.slice(1),
        name: type.name.charAt(0).toUpperCase() + type.name.slice(1)
      }
    }
    // Fallback for unexpected format
    return {
      value: String(type),
      label: String(type),
      name: String(type)
    }
  })
}

/**
 * Get store product types synchronously (returns cached types or default)
 * Use this when you need immediate access and types are likely cached
 * @returns {string[]} Array of product type strings
 */
export const getStoreProductTypesSync = () => {
  // If not initialized, start the initialization
  if (!initialized) {
    initialized = true
    getStoreProductTypes().catch(() => {
      // Silently handle errors
    })
  }
  
  return cachedProductTypes || [
    { name: "purchase", description: "This is a normal product purchase" },
    { name: "subscription", description: "Recurring payment product with auto-renewal" },
    { name: "license", description: "Product that grants access rights or permissions" },
    { name: "bundle", description: "Package containing multiple products together" },
    { name: "credits", description: "Virtual currency or credit pack" }
  ]
}

/**
 * Get store product types for CMS options (attempts fresh fetch if not cached)
 * @returns {Array<{value: string, name: string, description?: string}>} Array of formatted options for CMS dropdowns
 */
export const getStoreProductTypesForCMS = () => {
  // If not cached, trigger a fetch (but don't wait for it)
  if (!cachedProductTypes) {
    getStoreProductTypes().catch(() => {
      // Silently handle errors
    })
  }
  
  const types = cachedProductTypes || [
    { name: "purchase", description: "This is a normal product purchase" },
    { name: "subscription", description: "Recurring payment product with auto-renewal" },
    { name: "license", description: "Product that grants access rights or permissions" },
    { name: "bundle", description: "Package containing multiple products together" },
    { name: "credits", description: "Virtual currency or credit pack" }
  ]

  // Handle both old format (array of strings) and new format (array of objects)
  return types.map(type => {
    if (typeof type === "string") {
      // Legacy format - convert string to object
      return {
        value: type,
        name: type.charAt(0).toUpperCase() + type.slice(1)
      }
    } else if (type && typeof type === "object" && type.name) {
      // New format - object with name and description
      return {
        value: type.name,
        name: type.name.charAt(0).toUpperCase() + type.name.slice(1),
        description: type.description
      }
    }
    return {
      value: type,
      name: String(type)
    }
  })
}


/**
 * Clear the cache (useful for testing or when parameter is updated)
 */
export const clearProductTypesCache = () => {
  cachedProductTypes = null
  loadingPromise = null
  initialized = false
}

/**
 * Force refresh product types from server (clears cache and re-fetches)
 * @returns {Promise<string[]>} Fresh array of product type strings
 */
export const refreshProductTypes = async () => {
  clearProductTypesCache()
  return await getStoreProductTypes()
}

/**
 * Get product types for CMS field (returns cached or defaults)
 * @returns {string[]} Array of product type strings
 */
export const getProductTypesForCMSField = () => {
  console.log('[StoreProductTypes] CMS field getter called, cached types:', cachedProductTypes)
  
  // Return cached types if available, otherwise defaults
  const result = cachedProductTypes || [
    { name: "purchase", description: "This is a normal product purchase" },
    { name: "subscription", description: "Recurring payment product with auto-renewal" },
    { name: "license", description: "Product that grants access rights or permissions" },
    { name: "bundle", description: "Package containing multiple products together" },
    { name: "credits", description: "Virtual currency or credit pack" }
  ]
  console.log('[StoreProductTypes] Returning types for CMS:', result)
  return result
}

/**
 * Check if a given type is a valid product type
 * @param {string} type - The type to validate
 * @returns {boolean} True if valid product type
 */
export const isValidProductType = (type) => {
  const types = getStoreProductTypesSync()
  // Handle both old format (string array) and new format (object array)
  return types.some(t => typeof t === 'string' ? t === type : t.name === type)
}

/**
 * Get color mapping for product types (for UI display)
 * @param {string} type - The product type
 * @returns {string} CSS classes for styling
 */
export const getProductTypeColor = (type) => {
  const colorMap = {
    purchase: "bg-admin_accent_2 text-white",
    subscription: "bg-admin_accent_3 text-white",
    license: "bg-admin_accent_1 text-white",
    bundle: "bg-admin_accent_5 text-white",
    credits: "bg-admin_accent_4 text-white",
  }

  return colorMap[type] || "bg-admin_neutral_2 text-white border-admin_neutral_2"
}

export default {
  getStoreProductTypes,
  getStoreProductTypeOptions,
  getStoreProductTypesSync,
  clearProductTypesCache,
  isValidProductType,
  getProductTypeColor
}

// Note: Pre-loading removed because it requires user authentication
// The types will be loaded on first use when authentication is available